'use client'

import {
  getListPagination,
  EntityDetailSkeleton,
  ListBrowseToolbar,
  Button,
  Card,
  CardContent,
  ListPagination,
  useInviteToApply,
  sortProspectsWithGapClosersFirst,
  apiPagesForOffsetWindow,
  clampPage,
  stickyAwareListWindow,
  totalPages,
  useDebouncedValue,
  lifecycleInviteAvailable,
} from '@yeshuman/sdk'
import { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useParams } from 'react-router-dom'

import { CandidateCardSkeleton } from '../recruiter/candidate-card-skeleton'
import { InviteApplicantsByEmail } from './invite-applicants-by-email'
import { SourcingRow } from './sourcing-row'
import { useJobWorkspaceCandidatePanel } from '../../hooks/use-job-workspace-candidate-panel'
import { useJobProspectMatches, type JobProspectMatch } from '../../hooks/use-job-prospect-matches'
import { useJobAnalysisCoverage } from '../../hooks/use-job-analysis-coverage'
import {
  useEmailInviteMutations,
  useOpportunityEmailInvites,
  type EmailInviteSummary,
} from '../../hooks/use-opportunity-email-invites'
import { useApplicationNudge } from '../../hooks/use-application-nudge'
import { useOpportunityApplicants, type ApplicantCard } from '../../hooks/use-opportunity-applicants'
import { useOpportunity } from '../../hooks/use-organisation-opportunities'
import { JobWorkspaceSuggestionsEmpty } from './job-workspace-suggestions-empty'

const PROSPECT_SEARCH_DEBOUNCE_MS = 300
const jobInvitePagination = getListPagination('jobInvite')

type StickySourcingRow =
  | { kind: 'email'; invite: EmailInviteSummary }
  | { kind: 'invited'; applicant: ApplicantCard }

function JobWorkspaceSuggestionsLoading() {
  return (
    <div className="space-y-4" role="status" aria-live="polite" aria-busy="true">
      <div className="flex items-start gap-3 rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
        <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" aria-hidden />
        <div className="space-y-1">
          <p className="font-medium text-foreground">Finding people to invite</p>
          <p>The first load can take up to a minute. Later visits are much faster.</p>
        </div>
      </div>
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <CandidateCardSkeleton key={i} />
        ))}
      </div>
    </div>
  )
}

function filterActionableMatches(
  matches: JobProspectMatch[],
  appliedProfileIds: Set<string>,
  isInvited: (profileId: string) => boolean,
) {
  return matches.filter(
    (match) => !appliedProfileIds.has(match.profile_id) && !isInvited(match.profile_id),
  )
}

export function JobWorkspaceSuggestionsTab() {
  const { jobId = '' } = useParams<{ jobId: string }>()
  const { data: opportunity, isLoading: jobLoading } = useOpportunity(jobId)
  const candidatePanel = useJobWorkspaceCandidatePanel(jobId)
  const [searchInput, setSearchInput] = useState('')
  const [suggestionsPage, setSuggestionsPage] = useState(1)
  const debouncedSearch = useDebouncedValue(searchInput.trim(), PROSPECT_SEARCH_DEBOUNCE_MS)
  const paginate = jobInvitePagination.enabled
  const pageSize = jobInvitePagination.pageSize

  useEffect(() => {
    setSuggestionsPage(1)
  }, [debouncedSearch])

  const matchesQueryEnabled =
    !!jobId &&
    !jobLoading &&
    lifecycleInviteAvailable(opportunity?.publication_status)

  const { board } = useOpportunityApplicants(jobId)
  const { data: emailInvites = [], isLoading: emailInvitesLoading } = useOpportunityEmailInvites(
    jobId,
    matchesQueryEnabled,
  )
  const { data: coverage } = useJobAnalysisCoverage(jobId, matchesQueryEnabled)
  const { resendInvite, revokeInvite } = useEmailInviteMutations(jobId)
  const nudgeMutation = useApplicationNudge(jobId)

  const {
    appliedProfileIds,
    inviteProfile,
    isApplied,
    isInvited,
    isInviting,
    invitingProfileId,
  } = useInviteToApply(jobId)

  const stickyRows = useMemo<StickySourcingRow[]>(() => {
    const pendingEmail = emailInvites
      .filter((invite) => invite.status === 'pending')
      .map((invite) => ({ kind: 'email' as const, invite }))
    const invited = (board?.applicants ?? [])
      .filter((applicant) => applicant.status === 'invited')
      .map((applicant) => ({ kind: 'invited' as const, applicant }))
    return [...pendingEmail, ...invited]
  }, [board?.applicants, emailInvites])

  const stickyCount = stickyRows.length
  const listWindow = paginate
    ? stickyAwareListWindow(suggestionsPage, pageSize, stickyCount)
    : null
  const apiPages = listWindow
    ? apiPagesForOffsetWindow(listWindow.suggestionOffset, listWindow.suggestionLimit, pageSize)
    : []
  const primaryApiPage = apiPages[0] ?? 1
  const secondaryApiPage = apiPages[1]

  const {
    data: prospectData,
    isLoading: matchesLoading,
    error,
    refetch,
    isFetching,
  } = useJobProspectMatches(jobId, {
    enabled: matchesQueryEnabled,
    q: debouncedSearch,
    page: paginate ? primaryApiPage : undefined,
    pageSize: paginate ? pageSize : undefined,
  })
  const { data: prospectDataNext, isFetching: isFetchingNext } = useJobProspectMatches(jobId, {
    enabled: matchesQueryEnabled && paginate && secondaryApiPage != null,
    q: debouncedSearch,
    page: secondaryApiPage,
    pageSize,
  })

  const showMatchesLoading =
    matchesQueryEnabled &&
    (matchesLoading ||
      emailInvitesLoading ||
      (isFetching && !prospectData) ||
      (secondaryApiPage != null && isFetchingNext && !prospectDataNext) ||
      searchInput.trim() !== debouncedSearch)

  const suggestions = useMemo(() => {
    const mergedMatches = paginate
      ? [...(prospectData?.matches ?? []), ...(prospectDataNext?.matches ?? [])]
      : (prospectData?.matches ?? [])
    const raw = filterActionableMatches(mergedMatches, appliedProfileIds, isInvited)
    const sorted = debouncedSearch ? raw : sortProspectsWithGapClosersFirst(raw, coverage ?? null)
    if (!listWindow) return sorted

    const primaryOffset = (primaryApiPage - 1) * pageSize
    const windowStart = listWindow.suggestionOffset - primaryOffset
    return sorted.slice(windowStart, windowStart + listWindow.suggestionLimit)
  }, [
    appliedProfileIds,
    coverage,
    debouncedSearch,
    isInvited,
    listWindow,
    pageSize,
    paginate,
    primaryApiPage,
    prospectData?.matches,
    prospectDataNext?.matches,
  ])

  const suggestionsTotal = paginate
    ? (prospectData?.total ?? 0)
    : suggestions.length
  const sourcingTotal = stickyCount + suggestionsTotal
  const visibleStickyRows = listWindow
    ? stickyRows.slice(listWindow.stickyStart, listWindow.stickyStart + listWindow.stickyLimit)
    : stickyRows

  useEffect(() => {
    if (!paginate) return
    const maxPage = totalPages(sourcingTotal, pageSize)
    if (suggestionsPage > maxPage) setSuggestionsPage(maxPage)
  }, [pageSize, paginate, sourcingTotal, suggestionsPage])

  if (jobLoading) {
    return (
      <EntityDetailSkeleton variant="job" withPageContainer={false} ariaLabel="Loading suggestions" />
    )
  }

  if (!lifecycleInviteAvailable(opportunity?.publication_status)) {
    const msg =
      opportunity?.publication_status === 'archived'
        ? 'Restore this job to send invites.'
        : opportunity?.publication_status === 'closed'
          ? 'Re-open this job to send invites.'
          : 'Publish this job before inviting candidates.'
    return (
      <Card>
        <CardContent className="py-8 text-sm text-muted-foreground">{msg}</CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <ListBrowseToolbar
          searchId="job-invite-talent-search"
          searchPlaceholder="Search talent by name, headline, or skill…"
          searchValue={searchInput}
          onSearchChange={setSearchInput}
        />
        <p className="text-xs text-muted-foreground">
          Ranked suggestions for this role appear below. Search explores the wider talent network.
        </p>
      </div>

      <InviteApplicantsByEmail opportunityId={jobId} />

      {showMatchesLoading ? <JobWorkspaceSuggestionsLoading /> : null}

      {!showMatchesLoading && error ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-8 text-center text-sm">
            <p className="text-destructive">Could not load suggestions. Try again later.</p>
            <Button type="button" variant="outline" size="sm" disabled={isFetching} onClick={() => refetch()}>
              {isFetching ? 'Retrying…' : 'Retry'}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {!showMatchesLoading && !error && stickyCount === 0 && suggestionsTotal === 0 ? (
        <JobWorkspaceSuggestionsEmpty emptyReason={prospectData?.empty_reason} />
      ) : null}

      {!showMatchesLoading && !error && sourcingTotal > 0 ? (
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-foreground">
            Sourcing · {sourcingTotal}
          </h2>
          <div className="space-y-3">
            {visibleStickyRows.map((row) =>
              row.kind === 'email' ? (
                <SourcingRow
                  key={row.invite.id}
                  variant="email-pending"
                  invite={row.invite}
                  onResend={(inviteId) => resendInvite.mutate(inviteId)}
                  onRevoke={(inviteId) => revokeInvite.mutate(inviteId)}
                  resendPending={resendInvite.isPending}
                  revokePending={revokeInvite.isPending}
                />
              ) : (
                <SourcingRow
                  key={row.applicant.id}
                  variant="invited-pending"
                  applicant={row.applicant}
                  onNudge={(applicationId) => nudgeMutation.mutate(applicationId)}
                  onOpen={() => candidatePanel.openApplicant(row.applicant, 'suggestions')}
                  nudgePending={nudgeMutation.isPending}
                />
              ),
            )}
            {suggestions.map((match) => (
              <SourcingRow
                key={match.profile_id}
                variant="suggestion"
                match={match}
                coverage={coverage ?? null}
                onOpen={() => candidatePanel.openProspect(match, 'suggestions')}
                onInvite={inviteProfile}
                isApplied={isApplied(match.profile_id)}
                isInvited={isInvited(match.profile_id)}
                isInviting={isInviting && invitingProfileId === match.profile_id}
              />
            ))}
          </div>
          {paginate ? (
            <ListPagination
              page={clampPage(suggestionsPage, sourcingTotal, pageSize)}
              pageSize={pageSize}
              total={sourcingTotal}
              onPageChange={setSuggestionsPage}
            />
          ) : null}
        </section>
      ) : null}
    </div>
  )
}

/** Full sourcing list count for the Invite tab badge (sticky invites + suggestions). */
export function useInviteTabSuggestionsCount(jobId: string | undefined, enabled: boolean) {
  const queryEnabled = !!jobId && enabled
  const { data } = useJobProspectMatches(jobId, {
    enabled: queryEnabled,
    page: jobInvitePagination.enabled ? 1 : undefined,
    pageSize: jobInvitePagination.pageSize,
  })
  const { isInvited, selfAppliedProfileIds } = useInviteToApply(jobId)
  const { board } = useOpportunityApplicants(jobId)
  const { data: emailInvites = [] } = useOpportunityEmailInvites(jobId, queryEnabled)

  return useMemo(() => {
    const stickyCount =
      emailInvites.filter((invite) => invite.status === 'pending').length +
      (board?.applicants ?? []).filter((applicant) => applicant.status === 'invited').length
    if (jobInvitePagination.enabled) {
      return stickyCount + (data?.total ?? 0)
    }
    return (
      stickyCount +
      filterActionableMatches(data?.matches ?? [], selfAppliedProfileIds, isInvited).length
    )
  }, [board?.applicants, data?.matches, data?.total, emailInvites, isInvited, selfAppliedProfileIds])
}
